/**
 * THE MEDIA LIBRARY — the images and speech Scout actually generated.
 *
 * WHY THIS PAGE EXISTS. Image generation and text-to-speech were implemented
 * and written to the evidence bucket with a digest, a provider and a model, and
 * there was no way to see any of it from the workspace: the only route to an
 * artifact was to read a receipt, copy an R2 key and ask an operator. A
 * capability whose output the Owner cannot open is not operationally usable.
 *
 * WHAT IT REFUSES TO DO. It does not show a cached or remembered list — every
 * load lists the bucket, so a refused write cannot leave a phantom card. It
 * does not present an unavailable store as an empty library. And it does not
 * claim video generation: a video artifact appears only if some instrument
 * actually produced one, and today the Gemini media transport produces images
 * and speech; video is INPUT.
 *
 * THE BYTES ARE SERVED BY THE DEPLOYMENT, NOT BY A THIRD PARTY. Each player and
 * image loads the owner-scoped route, which re-checks the key's prefix. The
 * page holds no credential and cannot mint one.
 */

import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';
import { Failure } from './failure';
import { PageHeader, Technical } from './about';
import {
	listMedia,
	mediaFileUrl,
	type MediaArtifact,
	type MediaKind,
	type MediaLibraryPage,
} from '../media';
import type { GatewayError } from '../gateway';

const PAGE_SIZE = 40;

type Filter = 'ALL' | MediaKind;

const FILTERS: ReadonlyArray<{ id: Filter; label: string }> = [
	{ id: 'ALL', label: 'Everything' },
	{ id: 'IMAGE', label: 'Images' },
	{ id: 'AUDIO', label: 'Audio' },
	{ id: 'VIDEO', label: 'Video' },
];

function formatBytes(bytes: number): string {
	if (bytes < 1_024) return `${bytes} B`;
	if (bytes < 1_024 * 1_024) return `${(bytes / 1_024).toFixed(1)} KB`;
	return `${(bytes / (1_024 * 1_024)).toFixed(1)} MB`;
}

function formatWhen(value: string | null): string {
	if (value === null) return 'upload time not reported';
	const parsed = new Date(value);
	return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
}

function purposeLabel(purpose: string | null): string {
	switch (purpose) {
		case 'MODEL_TRIAL':
			return 'chat model trial';
		case 'RESEARCH_TRIAL':
			return 'pre-registered trial';
		case 'SPACE_TRIAL':
			return 'Research Space trial';
		default:
			return 'provenance not recorded';
	}
}

function identityOf(artifact: MediaArtifact): string {
	const provider = artifact.provider ?? 'provider not recorded';
	const model = artifact.model ?? 'model not recorded';
	return `${provider} / ${model}`;
}

function ArtifactPreview({ artifact }: { artifact: MediaArtifact }) {
	const url = mediaFileUrl(artifact.key);
	if (artifact.kind === 'IMAGE') {
		return (
			<a href={url} target="_blank" rel="noreferrer" class="media-preview">
				<img
					src={url}
					alt={`Generated image from ${identityOf(artifact)}`}
					loading="lazy"
					style="display:block; max-width:100%; height:auto; border-radius:6px;"
				/>
			</a>
		);
	}
	if (artifact.kind === 'AUDIO') {
		return (
			<audio
				controls
				preload="none"
				src={url}
				aria-label={`Generated audio from ${identityOf(artifact)}`}
				style="width:100%;"
			>
				This browser cannot play the clip. <a href={url}>Open it directly</a>.
			</audio>
		);
	}
	if (artifact.kind === 'VIDEO') {
		return (
			<video
				controls
				preload="metadata"
				src={url}
				aria-label={`Generated video from ${identityOf(artifact)}`}
				style="width:100%; border-radius:6px;"
			>
				This browser cannot play the clip. <a href={url}>Open it directly</a>.
			</video>
		);
	}
	return (
		<p class="small muted">
			{artifact.mediaType} is stored but is not a browser-renderable image, audio or video
			file. <a href={mediaFileUrl(artifact.key, { download: true })}>Download it</a>.
		</p>
	);
}

function ArtifactCard({ artifact }: { artifact: MediaArtifact }) {
	return (
		<li class="panel" style="padding:12px; margin:0;">
			<ArtifactPreview artifact={artifact} />
			<p class="small" style="margin:8px 0 4px;">
				<strong>{identityOf(artifact)}</strong>
			</p>
			<p class="small muted" style="margin:0 0 6px;">
				{purposeLabel(artifact.purpose)} · {formatWhen(artifact.uploadedAt)} ·{' '}
				{formatBytes(artifact.bytes)}
			</p>
			<p class="small" style="margin:0;">
				<a href={mediaFileUrl(artifact.key, { download: true })}>Download</a>
			</p>
			<Technical summary="Artifact record">
				<p class="small">
					kind: <code>{artifact.kind ?? 'unclassified'}</code> · media type:{' '}
					<code>{artifact.mediaType}</code>
				</p>
				<p class="small">
					sha256: <code>{artifact.sha256 ?? 'not recorded'}</code>
				</p>
				<p class="small">
					run: <code>{artifact.runId ?? 'none'}</code> · space:{' '}
					<code>{artifact.spaceId ?? 'Main Scout'}</code> · trial:{' '}
					<code>{artifact.trialId ?? 'none'}</code>
				</p>
				<p class="small">
					prompt hash: <code>{artifact.promptHash ?? 'not recorded'}</code>
				</p>
				<p class="small">
					store key: <code>{artifact.key}</code>
				</p>
			</Technical>
		</li>
	);
}

export function MediaView() {
	const [page, setPage] = useState<MediaLibraryPage | null>(null);
	const [artifacts, setArtifacts] = useState<MediaArtifact[]>([]);
	const [error, setError] = useState<GatewayError | null>(null);
	const [loading, setLoading] = useState(true);
	const [filter, setFilter] = useState<Filter>('ALL');

	const load = useCallback(async (cursor: string | null, append: boolean): Promise<void> => {
		setLoading(true);
		const result = await listMedia({ limit: PAGE_SIZE, cursor });
		if (result.ok) {
			setPage(result.data);
			setArtifacts((current) =>
				append ? [...current, ...result.data.artifacts] : result.data.artifacts,
			);
			setError(null);
		} else if (!append) {
			setPage(null);
			setArtifacts([]);
			setError(result.error);
		} else {
			// A FAILED "LOAD MORE" KEEPS WHAT IS ON SCREEN AND SAYS SO. Replacing
			// the page with an error would make a working first page look lost.
			setError(result.error);
		}
		setLoading(false);
	}, []);

	useEffect(() => {
		void load(null, false);
	}, [load]);

	const visible = useMemo(
		() => (filter === 'ALL' ? artifacts : artifacts.filter((item) => item.kind === filter)),
		[artifacts, filter],
	);

	const hasMore = page?.cursor !== null && page?.cursor !== undefined;

	return (
		<>
			<PageHeader
				id="media-heading"
				title="Media"
				about={{
					purpose:
						'The images and speech Scout generated, read from the artifact store that actually holds them.',
					how: (
						<p>
							Filter by kind, play or open a file, or download it. Each card names the
							provider and model that produced it, the instrument it came from, and the
							digest the receipt recorded.
						</p>
					),
					limits: (
						<p>
							This page lists generated OUTPUT only. Video is an INPUT to Scout today;
							there is no video-generation transport, so a video card appears only if a
							some other instrument actually stored one. An unavailable store is shown
							as unavailable, never as an empty library. An artifact that failed to
							persist never appears, because this list is the bucket and not a copy.
						</p>
					),
				}}
			/>

			{error !== null && page === null ? (
				<Failure error={error} onRetry={() => void load(null, false)} what="the media library" />
			) : null}

			{page !== null && !page.available ? (
				<div class="panel" role="status">
					<h2>The artifact store is not bound here</h2>
					<p>{page.reason ?? 'This deployment has no generated-media store.'}</p>
					<p class="small muted">
						Image generation and speech additionally require <code>GEMINI_API_KEY</code>{' '}
						and an Owner provider grant. The library reports the binding it actually has;
						it does not report a credential it cannot see as an empty store.
					</p>
				</div>
			) : null}

			{page !== null && page.available ? (
				<>
					<div class="toolbar" role="group" aria-label="Filter media by kind">
						{FILTERS.map((entry) => (
							<button
								key={entry.id}
								type="button"
								class={`button ghost small${filter === entry.id ? ' is-current' : ''}`}
								aria-pressed={filter === entry.id}
								onClick={() => setFilter(entry.id)}
							>
								{entry.label}
							</button>
						))}
					</div>

					<p class="small muted">
						{artifacts.length === 0
							? 'No generated media is on record for this Owner.'
							: `${artifacts.length} artifact${artifacts.length === 1 ? '' : 's'} loaded · ${page.scanned} stored object${page.scanned === 1 ? '' : 's'} examined`}
					</p>

					{page.truncated ? (
						<p class="panel small" role="status">
							The store holds more objects than one listing examines (limit{' '}
							{page.scanned.toLocaleString()}). Older artifacts may not be listed; this is
							a bounded read, not a statement that nothing else exists.
						</p>
					) : null}

					{artifacts.length === 0 ? (
						<p class="panel">
							Nothing has been generated in this workspace yet. A Gemini image or speech
							call that completes writes its bytes to the evidence bucket, and it appears
							here on the next load.
						</p>
					) : visible.length === 0 ? (
						<p class="panel">No {filter === 'ALL' ? '' : `${filter.toLowerCase()} `}artifact is in the loaded page.</p>
					) : (
						<ul
							class="media-grid"
							style="list-style:none; padding:0; margin:0; display:grid; grid-template-columns:repeat(auto-fill, minmax(260px, 1fr)); gap:12px;"
						>
							{visible.map((artifact) => (
								<ArtifactCard key={artifact.key} artifact={artifact} />
							))}
						</ul>
					)}

					{hasMore ? (
						<p>
							<button
								type="button"
								class="button ghost"
								disabled={loading}
								onClick={() => void load(page.cursor, true)}
							>
								{loading ? 'Loading…' : 'Load older artifacts'}
							</button>
						</p>
					) : null}

					{error !== null && page !== null ? (
						<p class="panel failure" role="alert">
							Loading more failed: {error.message}. The artifacts already shown are still
							the store’s answer for this page.
						</p>
					) : null}
				</>
			) : null}

			{page === null && error === null ? <p role="status">Reading the artifact store…</p> : null}
		</>
	);
}
